import {
  CommentFilters,
  Comment as CommentType,
  getCommentsConfidenceScore,
  setCommentsConfidenceScore,
  setCommentPinned,
  CommentStatus,
  deleteComments,
  deleteCommentsBulk,
  acceptComments,
  hideComments,
  revealComments,
  CommentProperty,
  setCommentLabel,
  CommentIds,
  getCommentPermalink,
} from "@/services/comments";
import { defineStore } from "pinia";

import {
  addCommentProperty,
  findCommentById,
  findCommmentsById,
  flatCommentThread,
  removeCommentProperty,
} from "@/utils/comments";
import { incrementRewardCounter } from "@/utils/rewardCounter";
import { delay } from "@/utils/delay";

type Comment = {
  checkedCommentIds: Set<string>;
  commentIds: CommentIds | null;
  highlightedCommentIds: string[];
  totalNumberOfComments: number | undefined;
  totalNumberOfFilteredComments: number;
  folderFilters: { name: string; filters: CommentFilters }[];
  loading: boolean;
  comments: CommentType[] | null;
  confidenceScore: number;
  visibleCommentIds: Set<string>;
  postId: string | null;
  postDetailInitialFilters: CommentFilters | null;
};

export type SocialMediaPageDetailsParameter = {
  pageExternalId: string;
  socialMediaTypeId: number;
  socialMediaConnectionId: number;
};

let abortController = new AbortController();

export const useCommentsStore = defineStore("comment", {
  state: (): Comment => {
    return {
      checkedCommentIds: new Set(),
      commentIds: null,
      highlightedCommentIds: [],
      totalNumberOfComments: undefined,
      totalNumberOfFilteredComments: 0,
      folderFilters: [],
      loading: false,
      comments: [],
      confidenceScore: -1,
      visibleCommentIds: new Set(),
      postId: null,
      postDetailInitialFilters: null,
    };
  },
  getters: {
    areAllVisibleCommentsSelected(): boolean {
      return [...this.visibleCommentIds].every((id) => this.checkedCommentIds.has(id));
    },
    allSelectableCommentIds(): string[] {
      if (!this.comments) return [];
      const ids: string[] = [];
      const traverse = (list: CommentType[]) => {
        for (const c of list) {
          ids.push(c.id);
          if (c.children?.length) traverse(c.children);
        }
      };
      traverse(this.comments);
      return ids;
    },
  },
  actions: {
    commitCheckedCommentId(commentId: string) {
      this.checkedCommentIds.add(commentId);
    },
    removeCheckedCommentId(commentId: string) {
      this.checkedCommentIds.delete(commentId);
    },
    setCommentStatus(comments: CommentType[], status: CommentStatus, includeChildren = false) {
      comments.forEach((comment) => {
        const hasChildren = comment.children.length > 0;
        if (includeChildren && hasChildren) {
          flatCommentThread([comment]).forEach((c) => (c.status = status));
        } else {
          comment.status = status;
        }
      });
    },

    setCommentVisibility(commentIds: string[], visible: boolean) {
      const comments = findCommmentsById(this.comments, commentIds);
      const oldValue = visible ? CommentProperty.NOT_VISIBLE : CommentProperty.VISIBLE;
      const newValue = visible ? CommentProperty.VISIBLE : CommentProperty.NOT_VISIBLE;
      comments.forEach((comment) => {
        removeCommentProperty(comment, oldValue);
        addCommentProperty(comment, newValue);
      });
    },

    async acceptComments(commentIds: string[], socialMediaType: number = 1) {
      const comments = findCommmentsById(this.comments, commentIds);

      const isInDeletion = (comment: CommentType) =>
        comment.status === CommentStatus.DELETED ||
        comment.status === CommentStatus.WILL_BE_DELETED ||
        comment.status === CommentStatus.ERROR_ON_DELETE;
      
      const commentsToAccept = comments.filter(c => !isInDeletion(c));
      try {
        await acceptComments(commentsToAccept.map(c => c.id), socialMediaType);
      } catch (error) {
        console.error(error);
        return;
      }
      incrementRewardCounter(commentsToAccept.length);
      this.setCommentStatus(commentsToAccept, CommentStatus.ACCEPTED);
    },

    async deleteCommentsBulk(commentIds: string[], socialMediaType: number = 1) {
      const comments = findCommmentsById(this.comments, commentIds);

      const isInDeletion = (comment: CommentType) =>
        comment.status === CommentStatus.DELETED ||
        comment.status === CommentStatus.WILL_BE_DELETED;
      
      const commentsToDelete = comments.filter(c => !isInDeletion(c));
      try {
        await deleteCommentsBulk(commentsToDelete.map(c => c.id), socialMediaType);
      } catch (error) {
        console.error(error);
        return;
      }
      incrementRewardCounter(commentsToDelete.length);
      this.setCommentStatus(commentsToDelete, CommentStatus.WILL_BE_DELETED);
    },

    async deleteComments(commentIds: string[], connectedPage: SocialMediaPageDetailsParameter, isMetaPlatform = false) {
      const comments = findCommmentsById(this.comments, commentIds);

      const isInDeletion = (comment: CommentType) =>
        comment.status === CommentStatus.DELETED ||
        comment.status === CommentStatus.WILL_BE_DELETED;

      const isIncludedInFamily = (comment: CommentType) => isMetaPlatform ? commentIds.includes(comment.parentId) : false;

      const commentsToDelete = comments.filter(c => !isInDeletion(c) && !isIncludedInFamily(c));

      try {
        if (commentsToDelete.length > 0) {
          const postExternalId = commentsToDelete[0].post.externalId;

          const { pageExternalId, socialMediaTypeId, socialMediaConnectionId } = connectedPage;

          const payload = {
            ids: commentsToDelete.map(c => c.id),
            socialMediaConnectionId,
            pageExternalId,
            socialMediaTypeId,
            postExternalId,
            priorityLevel: 1,
          };
          await deleteComments(payload);
        }
      } catch (error) {
        console.error(error);
        return;
      }

      incrementRewardCounter(commentsToDelete.length);
      this.setCommentStatus(commentsToDelete, CommentStatus.WILL_BE_DELETED, true);
    },

    async hideComments(commentIds: string[], socialMediaType: number = 1, isMetaPlatform = false) {
      const comments = findCommmentsById(this.comments, commentIds);

      const isAlreadyHidden = (comment: CommentType) =>
        comment.commentProperties.includes(CommentProperty.NOT_VISIBLE) ||
        comment.status === CommentStatus.HIDDEN_BY_PARENT ||
        comment.status === CommentStatus.WILL_BE_HIDDEN;

      const isIncludedInFamily = (comment: CommentType) => isMetaPlatform ? commentIds.includes(comment.parentId) : false;

      const commentsToHide = comments.filter(c => !isAlreadyHidden(c) && !isIncludedInFamily(c));
      if (commentsToHide.length === 0) return;

      await hideComments(commentsToHide.map(c => c.id), socialMediaType);
      incrementRewardCounter(commentsToHide.length);
      this.setCommentStatus(commentsToHide, CommentStatus.WILL_BE_HIDDEN, false);
    },

    async revealComments(commentIds: string[], socialMediaType: number = 1, isMetaPlatform = false) {
      const comments = findCommmentsById(this.comments, commentIds);

      const isNotHidden = (comment: CommentType) =>
        !comment.commentProperties.includes(CommentProperty.NOT_VISIBLE) &&
        comment.status !== CommentStatus.HIDDEN_BY_PARENT;

      const isAlreadyUnhiding = (comment: CommentType) => comment.status === CommentStatus.WILL_BE_UNHIDDEN;

      const isIncludedInFamily = (comment: CommentType) => isMetaPlatform ? commentIds.includes(comment.parentId) : false;

      const commentsToReveal = comments.filter(c => !isNotHidden(c) && !isAlreadyUnhiding(c) && !isIncludedInFamily(c));
      if (commentsToReveal.length === 0) return;

      await revealComments(commentsToReveal.map(c => c.id), socialMediaType);
      this.setCommentStatus(commentsToReveal, CommentStatus.WILL_BE_UNHIDDEN, false);
    },

    findCommentById(commentId: string) {
      return findCommentById(this.comments, commentId);
    },

    async setCommentPinned(commentId: string, isPinned: boolean, socialMediaType: number = 1) {
      // we apply the visual style first to provide our users an immediate effect after clicking the menu item
      const comment = this.findCommentById(commentId);
      if (!comment) {
        return;
      }

      const oldValue = isPinned ? CommentProperty.UNPINNED : CommentProperty.PINNED;
      const newValue = isPinned ? CommentProperty.PINNED : CommentProperty.UNPINNED;
      removeCommentProperty(comment, oldValue);
      addCommentProperty(comment, newValue);

      await setCommentPinned(commentId, isPinned, socialMediaType);
    },
    async setCommentLabel(commentId: string, labelName: string, labelId: number, isAdded: boolean) {
      const comment = this.findCommentById(commentId);
      if (!comment) {
        return;
      }
      if (!isAdded) {
        comment.labelIds = comment.labelIds.filter((item) => item !== labelId);
      } else comment.labelIds.push(labelId);

      await setCommentLabel(commentId, labelName, isAdded);
    },

    cancelCommentFetches() {
      abortController.abort("comment fetch canceled due to user action");
    },

    async fetchConfidenceScore() {
      try {
        const confidenceScore = await getCommentsConfidenceScore();
        this.confidenceScore = confidenceScore;
      } catch (error) {
        console.error(`could not fetch confidence score -> ${error}`);
      }
      return this.confidenceScore;
    },

    async setConfidenceScore(confidenceScore: number) {
      await setCommentsConfidenceScore(confidenceScore.toString());
      this.confidenceScore = confidenceScore;
    },

    async fetchCommentPermalink(commentId: string, externalCommentId: string, smt: number) {
      try {
        const response = await getCommentPermalink(commentId, externalCommentId, smt);
        if (response.isFound) {
          const comment = this.findCommentById(commentId);
          if (comment) {
            comment.permalinkUrl = response.permalinkUrl;
          }
        }
        return response;
      } catch (error) {
        console.error(`could not fetch comment permalink -> ${error}`);
        throw error;
      }
    },
  },
});
